// --- Import React and Hooks ---
import React, { useState, useEffect, useRef } from 'react';
import { useUser } from '@clerk/clerk-react'; // Assuming Clerk is used for user context
// Ensure this path correctly points to your Pidebar component file
import Pidebar from '../../assets/pilotsidebar';
import axios from 'axios';

// --- Import Lucide Icons - Merged & Cleaned List ---
import {
    LayoutDashboard, Settings, Users, Bot, Database, Link, BookOpen, Wrench, // Sidebar (Core) & Workflow
    FlaskConical, HelpCircle, FileUp, Zap, Rocket, // Sidebar/Workflow specific
    Search, Bell, ChevronDown, ChevronRight, ExternalLink, // Top Bar / General
    CheckCircle2, XCircle, Clock, Copy, Bookmark, Play, ArrowRight, // Status / Activity / Card Actions
    Target, /* BrainCircuit, MessageSquare, */ GitBranch, Share2, // Workflow Icons (Removed unused BrainCircuit, MessageSquare, Cpu, Wand2)
    DollarSign, BarChart3, // Icons (Potentially for future cost/metrics)
    Activity, // Agent Monitor (From Echtools Sidebar)
    Lightbulb, // Knowledge Optimizer (From Echtools - not used in content, but keeping import)
    Server, // Deployment Manager (From Echtools Sidebar)
    ListChecks, // Bulk Action Tool (From Echtools - not used in content)
    Gauge, // Performance Metrics (From Echtools - not used in content)
    Palette, // Theme/Visuals (From Echtools - not used in content)
    Menu, // Services Menu (From Echtools Topbar)
    Globe, // Region (From Echtools Topbar)
    LifeBuoy, // Support/Help (From Echtools Topbar)
    Plus, // Create (From Echtools)
    X, // Close (From Echtools)
    Eye, // View (From Echtools - for potential future use)
    Trash2, // Delete (From Echtools - for potential future use)
    PlugZap, // API Type (From Echtools - for potential future use)
    Loader2, // Loading (From Echtools & DeploymentItem)
    ShieldAlert, // Warning/Error (From Echtools & DeploymentItem)
    AlertCircle // For SOC 2 notification
} from 'lucide-react';

// --- Simple Date Formatting Placeholder ---
const formatRelativeTime = (dateString) => {
    if (!dateString) return 'N/A';
    try {
        const date = new Date(dateString);
        const now = new Date();
        const seconds = Math.round((now - date) / 1000);
        const minutes = Math.round(seconds / 60);
        const hours = Math.round(minutes / 60);
        const days = Math.round(hours / 24);
        const weeks = Math.round(days / 7);
        const months = Math.round(days / 30); // Approximate
        const years = Math.round(days / 365); // Approximate

        if (seconds < 60) return `${seconds}s ago`;
        if (minutes < 60) return `${minutes}m ago`;
        if (hours < 24) return `${hours}h ago`;
        if (days < 7) return `${days}d ago`;
        if (weeks < 4) return `${weeks}w ago`;
        if (months < 12) return `${months}mo ago`;
        return `${years}y ago`;
    } catch (e) {
        console.error("Error formatting date:", e);
        return "Invalid date";
    }
};

// --- Animation Utility Hook ---
const useOnScreen = (options = { threshold: 0.1 }) => {
    const ref = useRef();
    const [isVisible, setIsVisible] = useState(false);
    useEffect(() => {
        // Ensure IntersectionObserver is available
        if (typeof IntersectionObserver === 'undefined') {
            console.warn("IntersectionObserver not supported, animations triggered immediately.");
            setIsVisible(true);
            return;
        }
        const observer = new IntersectionObserver(([entry]) => {
            // Trigger if intersecting or if the element is already visible (covers edge cases)
            if (entry.isIntersecting) {
                setIsVisible(true);
                // Stop observing once visible to improve performance
                observer.unobserve(entry.target);
            }
        }, options);

        const currentRef = ref.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        // Cleanup function to unobserve
        return () => {
            if (currentRef && observer) {
                observer.unobserve(currentRef);
            }
        };
    }, [options, ref]); // Include ref in dependencies

    // Return ref callback and visibility state
    const hookRef = (node) => {
        ref.current = node;
    };
    return [hookRef, isVisible];
};

// --- Styled Clickable Components ---
const ClickableLink = ({ children, className, href = '#', target, rel, onClick, disabled, title, ...props }) => (
    <a
        href={!disabled ? href : undefined} target={target} rel={target === '_blank' ? 'noopener noreferrer' : rel}
        onClick={!disabled ? onClick : (e) => e.preventDefault()} role="button" tabIndex={disabled ? -1 : 0}
        className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`}
        aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props} >
        {children}
    </a>
);
const ClickableDiv = ({ children, className, onClick, disabled, title, ...props }) => (
    <div
        role="button" tabIndex={disabled ? -1 : 0}
        onClick={!disabled ? onClick : undefined}
        onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) onClick?.(e); }}
        className={`inline-flex items-center justify-center transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded-md ${ disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer' } ${className}`}
        aria-disabled={disabled} title={title || (typeof children === 'string' ? children : '')} {...props} >
        {children}
    </div>
);

// --- Top Bar Component ---
const TopBar = () => {
    // --- Get user data from Clerk ---
    const { user, isLoaded } = useUser();

    // --- Determine display name and initials ---
    let userDisplayName = "Loading...";
    let userInitials = "?";

    if (isLoaded && user) {
        // Prefer first name + last initial if available
        if (user.firstName) {
            userDisplayName = `${user.firstName}${user.lastName ? ` ${user.lastName.charAt(0)}.` : ''}`;
            userInitials = user.firstName.charAt(0).toUpperCase();
            if(user.lastName) {
                userInitials += user.lastName.charAt(0).toUpperCase();
            } else if (user.firstName.length > 1) {
                 userInitials += user.firstName.charAt(1).toUpperCase(); // Use second letter if no last name
            }
        }
        // Fallback to email address
        else if (user.primaryEmailAddress) {
            userDisplayName = user.primaryEmailAddress.emailAddress;
            userInitials = userDisplayName.charAt(0).toUpperCase();
            // Try to get a second initial from the part after '@' if simple
            const emailParts = userDisplayName.split('@');
            if(emailParts.length > 1 && emailParts[1].length > 0) {
                 userInitials += emailParts[1].charAt(0).toUpperCase();
            }
        }
        // Fallback for username
        else if (user.username) {
            userDisplayName = user.username;
            userInitials = userDisplayName.charAt(0).toUpperCase();
             if (userDisplayName.length > 1) {
                 userInitials += userDisplayName.charAt(1).toUpperCase();
            }
        }
        // Absolute fallback
        else {
             userDisplayName = "User";
             userInitials = "U";
        }
    } else if (isLoaded && !user) {
        userDisplayName = "Not Signed In";
        userInitials = "X";
    }
     // If not loaded, keep "Loading..."

    return (
        <div className="h-14 bg-gray-950 border-b border-gray-800/50 flex items-center justify-between px-4 flex-shrink-0 sticky top-0 z-20">
            {/* Left Side */}
            <div className="flex items-center space-x-3">
                {/* TODO: This title could be dynamic based on the active page */}
                <span className="text-sm font-medium text-white">Dashboard</span>
            </div>

            {/* Right Side */}
            <div className="flex items-center space-x-3 md:space-x-4">
                {/* Search Bar */}
                <div className="relative hidden md:block">
                    <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" strokeWidth={1.5}/>
                    <input type="search" placeholder="Search Foundry..." className="bg-gray-800/60 border border-gray-700/50 rounded-md pl-8 pr-2 py-1 w-48 lg:w-64 text-xs text-gray-200 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 focus:outline-none focus:bg-gray-800" />
                </div>

                {/* Region Indicator */}
                <ClickableDiv className="flex items-center text-xs text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50" title="Region">
                    <Globe className="w-4 h-4 mr-1" strokeWidth={1.5}/>
                    <span>us-east-1</span> {/* Replace with dynamic region if needed */}
                </ClickableDiv>

                {/* Support Link */}
                <ClickableLink href="https://metabees.org/foundry-docs" target="_blank" title="Support" className="text-gray-400 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                    <LifeBuoy className="w-5 h-5" strokeWidth={1.5}/>
                </ClickableLink>

                {/* Notifications */}
                <ClickableDiv title="Notifications" className="text-gray-400 hover:text-white relative p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                    <Bell className="w-5 h-5" strokeWidth={1.5}/>
                    {/* Optional: Add a notification badge */}
                    {/* <span className="absolute top-0 right-0 block h-1.5 w-1.5 rounded-full bg-red-500 ring-2 ring-gray-950"></span> */}
                </ClickableDiv>

                {/* Account Dropdown Trigger */}
                <ClickableDiv title="Account" className="flex items-center text-xs text-gray-300 hover:text-white p-1.5 -m-1.5 rounded hover:bg-gray-800/50">
                     {/* Display User's Name/Email */}
                    <span className="hidden md:inline mr-1.5 truncate max-w-[150px]">
                        {userDisplayName}
                    </span>
                     {/* Display User Initials Avatar */}
                    <span className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
                        {userInitials}
                    </span>
                    <ChevronDown className="w-4 h-4 ml-1 text-gray-500" strokeWidth={1.5}/>
                </ClickableDiv>
            </div>
        </div>
    );
};

// --- Main Content Area Components ---

const DashboardWelcome = ({ userName }) => (
    <div className="mb-10 relative">
        <h1 className="text-3xl font-bold text-white mb-1 relative z-10">
             {/* Use a more generic welcome if username is just 'User' */}
            {userName && userName !== "User" ? `Welcome back, ${userName}` : "Welcome to Echidna Foundry"}
        </h1>
        <p className="text-base text-gray-400 relative z-10">Let's build something intelligent today.</p>
    </div>
);

const StudioWorkflow = ({ isVisible }) => {
    const steps = [
        // Ensure these hrefs match your actual routes or remove them if they are just placeholders
        { icon: FileUp, title: "Add Knowledge", desc: "Upload files, link websites, or connect data.", href:"/echidna/efoundry/knowledge"},
        { icon: Zap, title: "Process Data", desc: "Index & vectorize sources for AI understanding.", href:"/echidna/efoundry/data"},
        { icon: Wrench, title: "Define Tools", desc: "Connect APIs to enable real-world actions.", href:"/echidna/efoundry/tools"},
        { icon: Rocket, title: "Launch Assistant", desc: "Configure persona and deploy your AI.", href:"/echidna/efoundry/myagents"},
    ];
    return (
        <div className="mb-12">
            <h3 className={`text-lg font-semibold text-gray-100 mb-5 transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>Get Started Workflow</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {steps.map((step, index) => (
                    <a href={step.href} key={step.title} className={`bg-gray-950 border border-gray-800/50 rounded-lg p-5 flex flex-col items-center text-center transition-all duration-500 ease-out hover:border-purple-600/40 hover:shadow-lg hover:-translate-y-1 block group ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{transitionDelay: `${100 + index * 100}ms`}}>
                        <div className="mb-3 text-purple-500 bg-purple-900/20 p-2.5 rounded-full border border-purple-800/50 transition-colors group-hover:bg-purple-900/30 group-hover:border-purple-700/70 group-hover:text-purple-400">
                            <step.icon className="w-5 h-5 transition-transform group-hover:scale-110" strokeWidth={1.5} />
                        </div>
                        <div>
                            <h4 className="text-sm font-semibold text-gray-100 mb-1 transition-colors group-hover:text-purple-300">{step.title}</h4>
                            <p className="text-xs text-gray-400">{step.desc}</p>
                        </div>
                    </a>
                ))}
            </div>
        </div>
    );
};

// --- Active Deployments Section ---
const DeploymentItem = ({ name, status, type, lastUpdate, isVisible, delay }) => {
    const statusStyles = {
        running: { icon: Zap, color: 'text-green-400', bg: 'bg-green-900/30', text: 'Active' },
        stopped: { icon: XCircle, color: 'text-gray-500', bg: 'bg-gray-800/50', text: 'Stopped' },
        error: { icon: ShieldAlert, color: 'text-red-500', bg: 'bg-red-900/30', text: 'Error' },
        pending: { icon: Clock, color: 'text-yellow-400', bg: 'bg-yellow-900/30', text: 'Pending' },
        deploying: { icon: Loader2, color: 'text-blue-400', bg: 'bg-blue-900/30', text: 'Deploying', animate: true },
    };
    // Default to 'stopped' if status is unknown or null/undefined
    const currentStatus = statusStyles[status?.toLowerCase()] || statusStyles.stopped;
    const StatusIcon = currentStatus.icon;
    const relativeLastUpdate = formatRelativeTime(lastUpdate);

    // Ensure name is a string and handle potential special characters in URL
    const safeName = typeof name === 'string' ? name : 'unknown';
    const encodedName = encodeURIComponent(safeName);
    // Construct the target URL
    const deploymentUrl = `/echidna/efoundry/deployments`;

    return (
        <div className={`bg-gray-950 border border-gray-800/50 rounded-lg px-4 py-3 flex items-center justify-between transition-all duration-500 ease-out ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-5'}`} style={{ transitionDelay: `${delay}ms` }}>
            <div className="flex items-center space-x-3 flex-1 min-w-0">
                <div className={`p-1.5 rounded-full ${currentStatus.bg} flex-shrink-0`}>
                    <StatusIcon className={`w-4 h-4 ${currentStatus.color} ${currentStatus.animate ? 'animate-spin' : ''}`} strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-100 truncate" title={safeName}>{safeName || 'Unnamed Agent'}</p>
                    <p className="text-xs text-gray-500 truncate">{type || 'N/A'}</p>
                </div>
            </div>
            <div className="text-right flex items-center space-x-3 ml-4 flex-shrink-0">
                <div className="hidden sm:block"> {/* Hide status text on very small screens */}
                    <p className={`text-xs font-medium ${currentStatus.color}`}>{currentStatus.text}</p>
                    <p className="text-xs text-gray-500">Created: {relativeLastUpdate}</p>
                </div>
                 {/* Make Manage button lead to the specific deployment page */}
                <ClickableLink
                    href={deploymentUrl}
                    title={`Manage Deployment: ${safeName}`}
                    className="p-1 text-gray-500 hover:text-purple-400 hover:bg-gray-800/50 rounded cursor-pointer"
                >
                    <Settings className="w-4 h-4" strokeWidth={1.5} />
                </ClickableLink>
            </div>
        </div>
    );
};

const ActiveDeployments = ({ isVisible }) => {
    const { user, isLoaded } = useUser();
    const [deployments, setDeployments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const API_BASE_URL = "https://web-production-5ee8.up.railway.app";

    useEffect(() => {
        let isActive = true;

        if (isVisible && isLoaded) {
             if (!user?.id) {
                 if (isActive) {
                    setIsLoading(false);
                    setDeployments([]);
                    setError(null);
                 }
                 return;
             }

            const fetchDeployments = async () => {
                if(isActive) {
                    setIsLoading(true);
                    setError(null);
                }
                try {
                    const response = await axios.post(`${API_BASE_URL}/echidna/myagents`, 
                        { cid: user.id },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                            }
                        }
                    );

                    if (!isActive) return;

                    if (isActive) {
                        setDeployments(response.data.agents || []);
                    }

                } catch (err) {
                    if (!isActive) return;
                    const errorMessage = err.response?.data?.message || err.message || 'An unexpected error occurred';
                    setError(errorMessage);
                    setDeployments([]);
                } finally {
                    if (isActive) setIsLoading(false);
                }
            };

            fetchDeployments();
        }

        return () => { isActive = false; };
    }, [isVisible, user?.id, isLoaded]);

    return (
        <div className="h-full flex flex-col">
            <h3 className={`text-lg font-semibold text-gray-100 mb-5 transition-opacity duration-700 flex-shrink-0 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>My Agents (Active Deployments)</h3>
            <div className="space-y-3 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-gray-950 flex-grow pr-1 max-h-96 min-h-[100px]">
                {isLoading && (
                    <div className="flex items-center justify-center p-4 text-gray-500">
                        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading agents...
                    </div>
                )}
                {!isLoading && error && (
                    <div className="flex items-center p-4 bg-red-900/20 border border-red-700/50 rounded-md text-red-400">
                        <ShieldAlert className="w-5 h-5 mr-2 flex-shrink-0" />
                        <span className="text-xs">Error loading agents: {error}</span>
                    </div>
                )}
                {!isLoading && !error && !user && isLoaded && (
                    <div className="text-center p-4 text-gray-500 text-sm">
                        Please sign in to view your agents.
                    </div>
                )}
                {!isLoading && !error && user && deployments.length === 0 && (
                    <div className="text-center p-4 text-gray-500 text-sm">
                        No agents found. Start by creating one!
                    </div>
                )}
                {!isLoading && !error && deployments.length > 0 && deployments.map((agent, index) => (
                    <DeploymentItem
                        key={agent._id || index}
                        name={agent.agentName}
                        status={agent.status || "running"}
                        type={agent.type || "Assistant"}
                        lastUpdate={agent.createdAt || agent.updatedAt}
                        isVisible={isVisible}
                        delay={100 + index * 75}
                    />
                ))}
            </div>
        </div>
    );
};


// --- Other Section Components ---

const WorkflowNode = ({ icon: Icon, title, active = false }) => (
    <div className={`bg-gray-900 border ${active ? 'border-purple-600 shadow-md shadow-purple-900/20 scale-105' : 'border-gray-700/50 opacity-70'} rounded-md p-2.5 w-28 text-center transition-all duration-300`}>
        <Icon className={`w-5 h-5 mx-auto mb-1 ${active ? 'text-purple-400' : 'text-gray-500'}`} strokeWidth={1.5} />
        <p className={`text-xs font-medium ${active ? 'text-gray-100' : 'text-gray-400'}`}>{title}</p>
    </div>
);

const AgentWorkflowCanvas = ({ isVisible }) => {
     return (
        <div className="mb-12">
            <h3 className={`text-lg font-semibold text-gray-100 mb-5 transition-opacity duration-700 delay-400 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>Define AI Chains / Workflows</h3>
             <div className={`bg-gray-950 border border-gray-800/50 rounded-lg p-6 transition-all duration-700 ease-out ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`} style={{transitionDelay: '500ms'}}>
                 <p className="text-xs text-center text-gray-500 mb-6">(Conceptual: Visually connect agents and tools)</p>
                 <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-6 relative">
                     {/* Nodes and Lines */}
                     <div className="relative z-10"><WorkflowNode icon={Users} title="User Input" active /></div>
                     <div className="h-px w-8 bg-gray-700 hidden md:block"></div>
                     <div className="relative z-10"><WorkflowNode icon={Wrench} title="CRM Tool" /></div>
                     <div className="h-px w-8 bg-gray-700 hidden md:block"></div>
                     <div className="relative z-10"><WorkflowNode icon={BookOpen} title="Knowledge" /></div>
                     <div className="h-px w-8 bg-gray-700 hidden md:block"></div>
                     <div className="relative z-10"><WorkflowNode icon={Bot} title="Synthesize" active/></div>
                     <div className="h-px w-8 bg-gray-700 hidden md:block"></div>
                     <div className="relative z-10"><WorkflowNode icon={Share2} title="Final Output" /></div>
                 </div>
            </div>
       </div>
    );
};

const ResourceCard = ({ icon: Icon, title, description, link, isVisible, delay }) => (
    <ClickableLink href={link || '#'} target="_blank" rel="noopener noreferrer" className={`bg-gray-950 rounded-lg border border-gray-800/50 p-4 flex flex-col transition-all duration-500 ease-out hover:border-purple-600/50 hover:shadow-lg hover:-translate-y-1 group block text-left ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ transitionDelay: `${delay}ms` }} role="article" >
        <div className="flex items-center mb-3">
            <div className="p-2 bg-purple-600/10 border border-purple-800/50 rounded-md mr-3 text-purple-400 transition-colors group-hover:bg-purple-600/20 group-hover:text-purple-300">
                <Icon className="w-5 h-5" strokeWidth={1.5}/>
            </div>
            <h4 className="text-sm font-semibold text-gray-100 flex-grow truncate transition-colors group-hover:text-purple-300">{title}</h4>
        </div>
        <p className="text-xs text-gray-400 mb-4 flex-grow">{description}</p>
        <div className="mt-auto text-right text-xs font-medium text-purple-400 group-hover:text-purple-300 transition-colors flex items-center justify-end">
            Explore Example <ArrowRight className="w-3 h-3 ml-1 transition-transform group-hover:translate-x-1" strokeWidth={1.5}/>
        </div>
    </ClickableLink>
);
const PrebuiltResources = ({ isVisible }) => {
     return (
        <div className={`transition-opacity duration-700 delay-200 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <h3 className="text-lg font-semibold text-gray-100 mb-4">Explore Examples</h3>
            <p className="text-sm text-gray-400 mb-6">Jumpstart your build process with these pre-configured examples.</p>
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                 {[
                     { icon: Users, title: 'Example Support Agent', description: 'Handles common customer questions based on a sample KB.', link: '/demo/support-agent' },
                     { icon: Wrench, title: 'Example API Tool', description: 'Demonstrates connecting an external API for actions.', link: '/demo/api-tool'},
                     { icon: BookOpen, title: 'Example Knowledge Base', description: 'A sample processed knowledge base ready for use.', link: '/demo/knowledge-base'}
                 ].map((resource, index) => (
                     <ResourceCard key={resource.title} {...resource} isVisible={isVisible} delay={300 + index * 150} />
                 ))}
             </div>
        </div>
    );
};
// --- End Other Section Components ---


// --- Main Content Component (Integrates all sections) ---
const MainContent = () => {
    // Hooks for animations
    const [refWorkflow, isVisibleWorkflow] = useOnScreen({ threshold: 0.05 });
    const [refDeployments, isVisibleDeployments] = useOnScreen({ threshold: 0.05 });
    const [refCanvas, isVisibleCanvas] = useOnScreen({ threshold: 0.1 });
    // const [refAbTest, isVisibleAbTest] = useOnScreen({ threshold: 0.05 }); // Removed
    // const [refOptimizer, isVisibleOptimizer] = useOnScreen({ threshold: 0.05}); // Removed
    // const [refVisualizer, isVisibleVisualizer] = useOnScreen({ threshold: 0.1 }); // Removed
    const [refResources, isVisibleResources] = useOnScreen({ threshold: 0.05 });

    const { user, isLoaded } = useUser(); // Get user and loading state

     // Determine user name for welcome message, handle loading state
    let userName = "User"; // Default
    if (isLoaded && user) {
        // Try different properties for the name
        if (user.firstName) {
            userName = user.firstName;
        } else if (user.fullName) {
            userName = user.fullName.split(' ')[0]; // Get first name from full name if available
        } else if (user.primaryEmailAddress) {
            userName = user.primaryEmailAddress.emailAddress.split('@')[0]; // Use part before @ as a fallback name
        } else if (user.username) {
            userName = user.username;
        }
    } else if (!isLoaded) {
        userName = "Loading..."; // Indicate loading if Clerk hasn't loaded user yet
    }


    return (
        <main className="flex-1 p-6 lg:p-8 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black relative z-10">
            {/* Subtle background pattern */}
            <div className="absolute inset-0 z-0 opacity-[0.02] bg-[linear-gradient(to_right,theme(colors.gray.700/50)_1px,transparent_1px),linear-gradient(to_bottom,theme(colors.gray.700/50)_1px,transparent_1px)] bg-[size:30px_30px] animate-pan-slow"></div>

            <div className='relative z-10'> {/* Content wrapper */}
                {/* Render all dashboard sections - Modify this if using conditional rendering based on sidebar state */}
                <DashboardWelcome userName={userName}/>

                <div ref={refWorkflow}> <StudioWorkflow isVisible={isVisibleWorkflow} /> </div>
                <div ref={refDeployments} className="mb-12"> <ActiveDeployments isVisible={isVisibleDeployments} /> </div>
                <div ref={refCanvas}> <AgentWorkflowCanvas isVisible={isVisibleCanvas} /> </div>
                {/* <div ref={refAbTest}> <PersonaAbTesting isVisible={isVisibleAbTest} /> </div> */} {/* Removed */}
                {/* <div ref={refOptimizer}> <CostOptimizer isVisible={isVisibleOptimizer} /> </div> */} {/* Removed */}
                {/* <div ref={refVisualizer}> <AiVisualizer isVisible={isVisibleVisualizer} /> </div> */} {/* Removed */}
                <div ref={refResources}> <PrebuiltResources isVisible={isVisibleResources} /> </div>
            </div>
        </main>
    );
}

// --- SOC 2 Notification Component ---
const Soc2Notification = () => {
    const [isVisible, setIsVisible] = useState(true);

    if (!isVisible) return null;

    return (
        <div className="fixed top-4 right-4 z-50 max-w-md bg-gray-900 border border-gray-800 rounded-lg shadow-lg p-4 animate-fade-in-fast">
            <div className="flex items-start">
                <div className="flex-shrink-0">
                    <AlertCircle className="w-5 h-5 text-blue-400" strokeWidth={1.5} />
                </div>
                <div className="ml-3 flex-1">
                    <h3 className="text-sm font-medium text-white">Your Security is Our Priority</h3>
                    <p className="mt-1 text-xs text-gray-400">
                        We're currently undergoing SOC 2 compliance to enhance your data security and service reliability. This means we're taking extra care with deployments and infrastructure changes to ensure your experience remains secure and stable. While this might temporarily affect deployment speeds, it's a crucial step in building a more robust platform for you.
                    </p>
                    <p className="mt-2 text-xs text-blue-400">
                        Your trust means everything to us. Thank you for your patience as we strengthen our security framework.
                    </p>
                </div>
                <ClickableDiv 
                    onClick={() => setIsVisible(false)}
                    className="ml-4 flex-shrink-0 text-gray-400 hover:text-white focus:outline-none"
                    title="Dismiss notification"
                >
                    <X className="w-4 h-4" strokeWidth={1.5} />
                </ClickableDiv>
            </div>
        </div>
    );
};

// --- Main Application Component ---
function EFoundry() {
    // State for the active sidebar item
    const [activeSidebarItem, setActiveSidebarItem] = useState('Dashboard');

    // Log state changes for debugging (optional)
    useEffect(() => {
        console.log("EFoundry: Active sidebar item state is now:", activeSidebarItem);
    }, [activeSidebarItem]);

    return (
        <div className="flex h-screen bg-black font-sans antialiased text-gray-200">
            {/* Pass state and setter function as props to Pidebar */}
            <Pidebar
                activeItem={activeSidebarItem}      // Current active item name
                setActiveItem={setActiveSidebarItem} // Function to update the active item
            />
            <div className="flex-1 flex flex-col overflow-hidden">
                <TopBar />        {/* TopBar now gets user info internally */}
                <MainContent />   {/* MainContent shows dashboard sections */}
                <Soc2Notification /> {/* Add SOC 2 notification */}
            </div>
            {/* Global styles */}
            <style jsx global>{`
                /* Scrollbar styles */
                .scrollbar-thin { scrollbar-width: thin; scrollbar-color: rgba(75, 85, 99, 0.5) transparent; }
                .scrollbar-thin::-webkit-scrollbar { width: 5px; height: 5px; }
                .scrollbar-thin::-webkit-scrollbar-thumb { background-color: rgba(75, 85, 99, 0.5); border-radius: 3px; }
                .scrollbar-thin::-webkit-scrollbar-track { background-color: transparent; }

                /* Ensure body bg matches */
                body { background-color: black; margin: 0; }

                /* Animations */
                @keyframes bounce-quick { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-4px); } }
                @keyframes pan-slow { 100% { background-position: -200% -200%; } }
                .animate-pan-slow { animation: pan-slow 30s linear infinite; background-position: 0 0; }
                @keyframes fade-in-fast { from { opacity: 0; } to { opacity: 1; } }
                .animate-fade-in-fast { animation: fade-in-fast 0.2s ease-out forwards; }

                /* Tailwind theme usage in CSS (example - replace if not using Tailwind CSS-in-JS/plugins) */
                /* Ensure this selector exists if using theme() in CSS */
                .bg-\\[linear-gradient\\(to_right\\,theme\\(colors\\.gray\\.700\\/50\\)_1px\\,transparent_1px\\)\\,linear-gradient\\(to_bottom\\,theme\\(colors\\.gray\\.700\\/50\\)_1px\\,transparent_1px\\)\\] {
                   /* Replace with actual gradient if not using Tailwind theme() function */
                   background-image: linear-gradient(to right, rgba(55, 65, 81, 0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(55, 65, 81, 0.5) 1px, transparent 1px);
                }


            `}</style>
        </div>
    );
}

// Export the combined component as EFoundry
export default EFoundry;